
import { defineComponent, ref, reactive, onMounted, computed } from 'vue'
import { stockpool } from '@/api/index'
import { useRoute } from 'vue-router'
import moment from 'moment'
import WebSocket from '@/components/websocket2.vue'
import ProductCard from './components/ProductCard.vue'
import TabBar from './components/TabBar.vue'
import FilterPanel from './components/FilterPanel.vue'
import PoolTable from './components/PoolTable.vue'
import { getCourseConfig, getSortFieldMap, getWsListKey } from './components/courseConfig'
import { formatChipMasterUpdateTime } from './components/chipMasterUtils'
import { formatMengzhuUpdateTime } from './components/mengzhuTrendUtils'

export default defineComponent({
  components: {
    WebSocket,
    ProductCard,
    TabBar,
    FilterPanel,
    PoolTable
  },
  setup() {
    const route = useRoute()

    // 主题：dark 或 light
    // isModalTheme=true 时强制使用 light，否则根据 theme 参数判断
    const isModalTheme = String(route.query.isModalTheme) === 'true'
    const theme = ref(isModalTheme ? 'light' : ((route.query.theme as string) || 'light'))
    console.log('isModalTheme:', route.query.isModalTheme, 'result:', isModalTheme, 'theme:', theme.value)
    // 当前课程编号，默认四维猎手
    const currentCourseNo = ref((route.query.courseNo as string) || 'e4408ecedc054856946546a185e8c047')

    // isClick: 1=弹窗模式(只有双击) 2=内嵌模式(单击+双击)，默认2
    const isClick = ref(Number(route.query.isClick) || 2)

    // 获取当前课程配置
    const currentCourseConfig = ref(getCourseConfig(currentCourseNo.value))
    const currentWsListKey = getWsListKey(currentCourseNo.value)

    // 判断是否为筹码大师
    const isChipMaster = computed(() => currentCourseNo.value === '17d1f0b0296e4b40ada792c7aa1599ec')

    // 判断是否为盟主趋势
    const isMengzhuTrend = computed(() => currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f')

    // 盟主趋势顶级 Tab 状态：'signalPool' 或 'hotCases'
    const mengzhuMainTab = ref('signalPool')

    // ==================== 计算属性：简化模板绑定 ====================

    // 是否有产品数据
    const hasProductData = computed(() => productData.value && productData.value.length > 0)

    // 获取产品卡片的 props（提取公共属性）
    const getProductCardProps = (product: any) => ({
      courseNo: product.courseNo,
      poolName: product.poolName,
      poolNameImg: product.poolNameImg,
      poolNameImgDark: product.poolNameImgDark,
      remark: product.remark,
      tags: product.tags,
      content: product.content,
      disclaimer: product.disclaimer,
      poolIcon: product.poolIcon
    })

    // FilterPanel 组件的 props（统一管理筛选面板配置）
    const filterPanelProps = computed(() => ({
      // 基础数据
      updateTime: updateTime.value,
      historyDates: historyDates.value,
      theme: theme.value,
      // 功能开关（根据不同产品配置）
      showFilterBtn: currentCourseConfig.value.showFilter !== false,           // 是否显示筛选按钮
      showStockCount: currentCourseConfig.value.showStockCount === true,       // 是否显示股票数量
      stockCount: totalCount.value,
      showStockSearch: currentCourseConfig.value.showStockSearch === true,     // 是否显示股票搜索（筹码大师）
      showSelectedStockInfo: currentCourseConfig.value.showSelectedStockInfo === true, // 是否显示已选股票信息
      selectedStockCount: totalCount.value,
      selectedStockDescription: currentCourseConfig.value.selectedStockDescription || '',
      // 信号标签配置（四维猎手/箱体擒龙/筹码大师/盟主趋势各有不同）
      signalTabs: currentCourseConfig.value.signalTabs || { latest: '最新信号', history: '历史信号' },
      showHistoryDateSelect: !isChipMaster.value,  // 筹码大师不显示历史日期选择，盟主趋势信号池显示
      // 盟主趋势特有配置
      showOptionalFilter: currentCourseConfig.value.showOptionalFilter === true,
      mainTabs: currentCourseConfig.value.mainTabs,
      currentMainTab: mengzhuMainTab.value,
      isMengzhuTrend: isMengzhuTrend.value
    }))

    // PoolTable 组件的 props（统一管理表格配置）
    const poolTableProps = computed(() => ({
      dataSource: poolList.value,
      loading: loading.value,
      hasMore: hasMore.value,
      loadingMore: loadingMore.value,
      theme: theme.value,
      courseNo: currentCourseNo.value,
      productData: productData.value,
      isClick: isClick.value,
      defaultSortField: 'pxChangRate',   // 默认按涨幅排序
      defaultSortOrder: 'descend',       // 默认降序
      mengzhuMainTab: mengzhuMainTab.value,  // 盟主趋势顶级 Tab
      // 盟主趋势：用于判断最高涨幅和累计涨幅的显示逻辑
      mengzhuLastTradingDay: mengzhuLastTradingDay.value,
      mengzhuUpdateTime: updateTime.value,
      mengzhuSignalType: signalType.value
    }))

    // 筹码大师交易日和更新时间
    const chipMasterTradingDay = ref<number>(0)
    const chipMasterUpdateTime = ref<number>(0)

    // 产品数据 - 从远程接口获取
    const productData = ref<any[]>([])

    // 获取产品数据（根据当前 courseNo 配置化筛选）
    async function fetchProductData() {
      try {
        const res = await stockpool.getProductConfig()
        console.log('fetchProductData res:', res)

        // 兼容不同的数据结构：res.data 或 res 本身
        const dataList = res.data

        if (dataList && Array.isArray(dataList)) {
          // 根据当前 courseNo 筛选对应的产品数据
          const filteredData = dataList.filter((item: any) => item.courseNo === currentCourseNo.value)
          console.log('fetchProductData filteredData:', filteredData, 'courseNo:', currentCourseNo.value)
          productData.value = filteredData
        } else {
          console.warn('fetchProductData: 数据格式不正确', dataList)
          productData.value = []
        }
      } catch (error) {
        console.error('fetchProductData failed:', error)
        productData.value = []
      }
    }

    const activeTab = ref(0)

    // PoolTable 组件引用
    const poolTableRef = ref<any>(null)

    // 四维猎手表格数据
    const poolList = ref<any[]>([])
    const loading = ref(false)
    const updateTime = ref('')
    const historyDates = ref<string[]>([])
    // 滚动加载状态
    const hasMore = ref(true)
    const loadingMore = ref(false)
    const totalCount = ref(0)

    // 盟主趋势：最后一个有效交易日（用于判断最高涨幅和累计涨幅的显示逻辑）
    const mengzhuLastTradingDay = ref('')

    // 请求序列号，用于处理快速点击排序时的竞态条件
    // 只有最新请求的响应才会被处理，旧请求的响应会被忽略
    let requestSequence = 0

    // 盟主趋势 Tab 切换防抖定时器
    let mainTabChangeTimer: ReturnType<typeof setTimeout> | null = null
    const MAIN_TAB_CHANGE_DELAY = 300 // 防抖延迟时间（毫秒）

    // 盟主趋势信号类型切换防抖定时器
    let signalTypeChangeTimer: ReturnType<typeof setTimeout> | null = null
    const SIGNAL_TYPE_CHANGE_DELAY = 300 // 防抖延迟时间（毫秒）

    // 筛选参数
    const filterParams = reactive({
      tradeDay: '',
      onlyOptional: 0,
      userName: '',
      isChose: 0,
      qs: 0,        // 趋势筛选：0:全部 1:强势 2:弱势
      zj: 0,        // 资金筛选：0:全部 1:强势 2:弱势
      tc: '',       // 题材筛选：0:全部，1:核心风口 2:普通风口 3:未入选，多选用逗号拼接
      sortField: '',
      sortType: '',
      pageNo: 1,
      pageSize: 100
    })

    // 信号类型：latest 或 history
    const signalType = ref('latest')

    function fresh() {
      handleRefresh()
    }

    // 获取交易日（使用通用的 getTradingDays API）
    const fetchTradingDays = async (time: number) => {

      console.log('fetchTradingDays 开始执行, time:', time)

      // 使用通用的交易日API
      const tokenRes = await stockpool.getToken()
      console.log('getToken 返回:', tokenRes.data.Data)

      const token = tokenRes.data.Data?.Token
      if (!token) {
        console.error('获取 Token 失败，返回数据:', tokenRes.data)
        return
      }

      const market = route.query.market || route.query.marketid || 'sz'

      // Get Trading Days - 根据配置获取API名称并调用
      const params = {
        market: market,
        limit: '12',
        isafter: 0,
      }

      console.log('Fetching trading days with params:', params, 'token:', token)

      // 根据配置获取交易日API名称并调用
      const tradingDaysApiName = currentCourseConfig.value.tradingDaysApi || 'getTradingDays'
      const tradingDaysApiFn = (stockpool as any)[tradingDaysApiName]

      if (typeof tradingDaysApiFn !== 'function') {
        console.error(`API ${tradingDaysApiName} not found in stockpool`)
        return
      }

      const daysRes = await tradingDaysApiFn.call(stockpool, params, token)
      console.log('daysRes:', daysRes)

      // 更新 historyDates - 将时间戳转换为日期格式
      if (daysRes.data && Array.isArray(daysRes.data.data)) {
        const allDates = daysRes.data.data.map((timestamp: number) => {
          return moment(timestamp * 1000).format('YYYY-MM-DD')
        })

        // 盟主趋势：根据 updateTime 过滤日期，历史信号只显示更新时间之前的日期
        if (isMengzhuTrend.value) {
          // 保存最后一个有效交易日（交易日列表的第一个，即最新的交易日）
          if (allDates.length > 0) {
            mengzhuLastTradingDay.value = allDates[0]
            console.log('盟主趋势 最后一个有效交易日:', mengzhuLastTradingDay.value)
          }

          // 获取 updateTime 的日期（只取日期部分，不含时间）
          const updateTimeDate = updateTime.value ? moment(updateTime.value).format('YYYY-MM-DD') : ''

          // 过滤掉 updateTime 当天及之后的日期，只保留 updateTime 之前的日期
          // 使用 <= 比较确保当天也被过滤掉
          let filteredDates = allDates
          if (updateTimeDate) {
            filteredDates = allDates.filter((date: string) => date < updateTimeDate)
          } else {
            // 如果没有 updateTime，使用当前日期过滤
            const todayDate = moment().format('YYYY-MM-DD')
            filteredDates = allDates.filter((date: string) => date < todayDate)
          }

          // 如果过滤后没有数据，使用原始数据（去掉第一个）
          if (filteredDates.length === 0 && allDates.length > 0) {
            filteredDates = allDates.slice(1)
          }

          // 如果还是没有数据，直接使用所有日期
          if (filteredDates.length === 0) {
            filteredDates = allDates
          }

          // 保持最多10个数据
          historyDates.value = filteredDates.slice(0, 10)
          console.log('盟主趋势 historyDates 更新为:', historyDates.value, 'updateTimeDate:', updateTimeDate, 'allDates:', allDates)
        } else {
          // 其他产品：根据 updateTime 过滤日期
          // 获取 updateTime 的日期
          const updateTimeDate = updateTime.value ? moment(updateTime.value).format('YYYY-MM-DD') : ''

          // 过滤掉 updateTime 当天及之后的日期，只保留 updateTime 之前的日期
          // 如果 updateTime 为空，则不过滤，直接使用所有日期
          let filteredDates = allDates
          if (updateTimeDate) {
            filteredDates = allDates.filter((date: string) => date < updateTimeDate)
          }

          // 如果过滤后没有数据，使用原始数据（去掉第一个，因为第一个通常是当天）
          if (filteredDates.length === 0 && allDates.length > 0) {
            filteredDates = allDates.slice(1)
          }

          // 如果还是没有数据，直接使用所有日期
          if (filteredDates.length === 0) {
            filteredDates = allDates
          }

          // 保持最多10个数据
          historyDates.value = filteredDates.slice(0, 10)
          console.log('historyDates 更新为:', historyDates.value, 'updateTimeDate:', updateTimeDate, 'allDates:', allDates)
        }
      }

    }

    // 获取股池数据（根据配置动态调用API）
    function getPoolData(isLoadMore = false) {
      // 增加请求序列号，用于处理快速点击排序时的竞态条件
      requestSequence++
      const currentSequence = requestSequence

      if (isLoadMore) {
        loadingMore.value = true
      } else {
        loading.value = true
      }

      // 根据不同产品构建不同的请求参数
      const isBoxDragon = currentCourseNo.value === '33130259066e4650bd0b7cf6b23d9488'
      const isChipMasterProduct = currentCourseNo.value === '17d1f0b0296e4b40ada792c7aa1599ec'
      const isMengzhuTrendProduct = currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f'

      let queryData: any = {}

      if (isMengzhuTrendProduct) {
        // 盟主趋势使用特定的参数结构（与四维猎手类似）
        queryData = {
          onlyOptional: filterParams.onlyOptional,
          sortField: filterParams.sortField || 'PxChangeRate',
          sortType: filterParams.sortType || 'desc',
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize
        }
        // 历史信号模式下传递 tradeDay
        if (filterParams.tradeDay) {
          queryData.tradeDay = filterParams.tradeDay
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          // queryData.tradeDay = filterParams.userName || route.query.userName || ''
        }
      } else if (isChipMasterProduct) {
        // 筹码大师使用特定的参数结构
        const signalTypeMap = currentCourseConfig.value.signalTypeMap || { latest: 'upBreak', history: 'strongSupport' }
        queryData = {
          trendType: signalTypeMap[signalType.value as 'latest' | 'history'] || 'upBreak',
          optional: filterParams.onlyOptional,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize,
          sortField: filterParams.sortField || 'pxChangeRate',
          sortType: filterParams.sortType || 'desc'
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          queryData.userName = filterParams.userName || route.query.userName || ''
        }
      } else if (isBoxDragon) {
        // 箱体擒龙使用不同的参数结构
        queryData = {
          sortField: filterParams.sortField,
          sortType: filterParams.sortType,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize,
          onlyOptional: filterParams.onlyOptional,
          strategyCode: 'great_wisdom_bs' // 箱体擒龙策略代码
        }
        if (filterParams.tradeDay) {
          queryData.tradingDay = filterParams.tradeDay
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          queryData.userName = filterParams.userName || route.query.userName || ''
        }
      } else {
        // 四维猎手等其他产品使用原有参数结构
        queryData = {
          tradeDay: filterParams.tradeDay,
          onlyOptional: filterParams.onlyOptional,
          userName: filterParams.userName || route.query.userName || '',
          isChose: filterParams.isChose,
          qs: filterParams.qs,
          zj: filterParams.zj,
          tc: filterParams.tc,
          sortField: filterParams.sortField,
          sortType: filterParams.sortType,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize
        }
        if (filterParams.tradeDay) {
          queryData.tradeDay = filterParams.tradeDay
        }
      }

      const data: any = {}
      data.data = queryData
      data.headers = {
        clienttype: 'pc',
        appVersion: route.query.appVersion,
        token: route.query.token,
        "Content-Type": "application/json"
      }

      // 根据配置获取API名称并调用
      const apiName = currentCourseConfig.value.requestApi
      const apiFn = (stockpool as any)[apiName]

      if (typeof apiFn !== 'function') {
        console.error(`API ${apiName} not found in stockpool`)
        loading.value = false
        loadingMore.value = false
        return
      }

      apiFn.call(stockpool, data).then((res: any) => {
        // 检查请求序列号，如果不是最新请求则忽略响应（解决快速点击排序时的竞态条件）
        if (currentSequence !== requestSequence) {
          console.log(`[排序] 忽略旧请求响应，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        // 盟主趋势：检查当前 Tab 是否仍然是 signalPool，避免数据错乱
        if (isMengzhuTrendProduct && mengzhuMainTab.value !== 'signalPool') {
          console.log(`[盟主趋势] 当前 Tab 已切换为 ${mengzhuMainTab.value}，忽略信号池响应`)
          loading.value = false
          loadingMore.value = false
          return
        }

        if (res.data) {
          let newList: any[] = []

          if (isMengzhuTrendProduct) {
            // 盟主趋势数据字段映射
            const rawList = res.data.poolList || []
            newList = rawList.map((item: any) => ({
              ...item,
              // 字段映射：API字段 -> 前端字段
              pxChangRate: item.pxChangeRate, // 涨幅
              plateName: item.plate?.name || '' // 题材板块名称
            }))
            totalCount.value = res.data.total || rawList.length || 0

            // 盟主趋势更新时间格式化（与四维猎手相同）
            if (res.data.updateTime) {
              updateTime.value = formatMengzhuUpdateTime(res.data.updateTime)
              // 只有在最新信号模式下才更新交易日列表，历史信号模式下不更新
              // 这样可以确保历史日期下拉框的内容与更新时间匹配
              if (signalType.value === 'latest') {
                const timestamp = res.data.updateTime > 9999999999
                  ? Math.floor(res.data.updateTime / 1000)
                  : res.data.updateTime
                fetchTradingDays(timestamp)
              }
            }
          } else if (isChipMasterProduct) {
            // 筹码大师数据字段映射
            const rawList = res.data.list || []
            newList = rawList.map((item: any) => ({
              ...item,
              // 字段映射：API字段 -> 前端字段
              pxChangRate: item.pxChangeRate, // 涨幅
            }))
            totalCount.value = res.data.total || rawList.length || 0

            // 保存交易日和更新时间用于格式化
            chipMasterTradingDay.value = res.data.tradingDay || 0
            chipMasterUpdateTime.value = res.data.updateTime || 0

            // 筹码大师更新时间格式化
            if (res.data.updateTime) {
              updateTime.value = formatChipMasterUpdateTime(res.data.updateTime, res.data.tradingDay)
            }
          } else if (isBoxDragon) {
            // 箱体擒龙数据字段映射
            const rawList = res.data.list || []
            // 创建现有数据的映射，用于保留 WebSocket 更新的字段
            const existingDataMap = new Map<string, any>()
            if (poolList.value && poolList.value.length > 0) {
              poolList.value.forEach((item: any) => {
                if (item && item.symbol) {
                  existingDataMap.set(item.symbol, item)
                }
              })
            }
            newList = rawList.map((item: any) => {
              const existingItem = existingDataMap.get(item.symbol)
              return {
                ...item,
                // 字段映射：API字段 -> 前端字段
                pxChangRate: item.pxChangeRate, // 涨幅
                highPct: item.highPct,          // 最大涨幅
                businessBalance: item.businessBalance, // 金额
                turnoverRatio: item.turnoverRatio,     // 换手
                // 总量：优先使用 API 返回的非零值，否则保留已有的 WebSocket 更新值
                volume: (item.volume && item.volume !== 0) ? item.volume : (existingItem?.volume || 0)
              }
            })
            // 箱体擒龙的 total 可能在 count 字段
            totalCount.value = res.data.count || rawList.length || 0
          } else {
            // 四维猎手等其他产品使用原有数据结构
            newList = res.data.poolList || []
            totalCount.value = res.data.total || 0
          }

          if (isLoadMore) {
            // 滚动加载时进行去重，避免出现重复股票
            const uniqueNewList = deduplicateStocks(poolList.value, newList)
            // 如果去重后没有新数据，说明已经没有更多数据了（防止无限加载）
            if (uniqueNewList.length === 0 && newList.length > 0) {
              hasMore.value = false
            } else {
              poolList.value = poolList.value.concat(uniqueNewList)
              // 如果返回的数据少于请求的 pageSize，说明已经没有更多数据了
              hasMore.value = newList.length >= filterParams.pageSize && poolList.value.length < totalCount.value
            }
          } else {
            // 首次加载也进行去重，去除 API 返回数据中的内部重复项
            poolList.value = deduplicateStocks([], newList)
            hasMore.value = poolList.value.length < totalCount.value
          }

          console.log('res.data.total:', totalCount.value, 'loaded:', poolList.value.length)

          // 处理更新时间（非筹码大师、非盟主趋势）
          // 盟主趋势的更新时间已在上面单独处理
          if (!isChipMasterProduct && !isMengzhuTrendProduct) {
            if (isBoxDragon) {
              // 箱体擒龙：使用 tradingDay 字段显示更新日期（YYYY-MM-DD）
              const resTradingDay = res.data.tradingDay
              if (resTradingDay) {
                updateTime.value = moment(resTradingDay).format('YYYY-MM-DD')
                // 只有在最新信号模式下才更新交易日列表，历史信号模式下不更新
                if (signalType.value === 'latest') {
                  const timestamp = resTradingDay > 9999999999
                    ? Math.floor(resTradingDay / 1000)
                    : resTradingDay
                  fetchTradingDays(timestamp)
                }
              } else {
                console.warn('getPoolData: tradingDay is missing for boxDragon')
              }
            } else {
              // 四维猎手等其他产品：使用 updateTime 字段
              const resUpdateTime = res.data.updateTime
              if (resUpdateTime) {
                updateTime.value = moment(resUpdateTime).format('YYYY-MM-DD HH:mm:ss')
                // 只有在最新信号模式下才更新交易日列表，历史信号模式下不更新
                if (signalType.value === 'latest') {
                  const timestamp = resUpdateTime > 9999999999
                    ? Math.floor(resUpdateTime / 1000)
                    : resUpdateTime
                  fetchTradingDays(timestamp)
                }
              } else {
                console.warn('getPoolData: updateTime is missing')
              }
            }
          }
        }
        loading.value = false
        loadingMore.value = false
      }).catch((err: any) => {
        // 检查请求序列号，如果不是最新请求则忽略错误处理
        if (currentSequence !== requestSequence) {
          console.log(`[排序] 忽略旧请求错误，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }
        console.error(`${apiName} failed`, err)
        loading.value = false
        loadingMore.value = false
      })
    }

    // 加载更多
    function handleLoadMore() {
      if (!hasMore.value || loadingMore.value || loading.value) return
      filterParams.pageNo += 1
      getPoolData(true)
    }

    // 信号类型变化（盟主趋势带防抖，防止快速切换导致显示问题）
    async function handleSignalTypeChange(type: string) {
      // 如果切换到相同的类型，直接返回
      if (signalType.value === type) return

      signalType.value = type

      // 筹码大师模式下，切换信号类型直接刷新数据（不需要防抖）
      if (isChipMaster.value) {
        resetListState()
        getPoolData()
      } else if (isMengzhuTrend.value) {
        // 盟主趋势模式下，添加防抖处理
        // 清除之前的防抖定时器
        if (signalTypeChangeTimer) {
          clearTimeout(signalTypeChangeTimer)
          signalTypeChangeTimer = null
        }

        // 防抖处理数据请求
        signalTypeChangeTimer = setTimeout(async () => {
          signalTypeChangeTimer = null
          if (type === 'history') {
            // 盟主趋势切换到历史信号时，只有在没有历史日期数据时才重新获取
            // 避免来回切换时导致下拉框内容减少
            if (historyDates.value.length === 0) {
              const currentTimestamp = Math.floor(Date.now() / 1000)
              await fetchTradingDays(currentTimestamp)
            }
            // 如果已有历史日期数据，FilterPanel 会自动选择第一个日期并触发 date-change
          } else {
            filterParams.tradeDay = ''
            resetListState()
            getPoolData()
          }
        }, SIGNAL_TYPE_CHANGE_DELAY)
      } else if (type === 'history') {
        // 四维猎手等其他产品，保持原有逻辑
        const currentTimestamp = Math.floor(Date.now() / 1000)
        await fetchTradingDays(currentTimestamp)
      } else {
        filterParams.tradeDay = ''
        resetListState()
        getPoolData()
      }
    }

    // 盟主趋势顶级 Tab 切换（带防抖，防止快速切换导致显示问题）
    function handleMainTabChange(tab: string) {
      // 如果切换到相同的 Tab，直接返回
      if (mengzhuMainTab.value === tab) return

      // 清除之前的防抖定时器
      if (mainTabChangeTimer) {
        clearTimeout(mainTabChangeTimer)
        mainTabChangeTimer = null
      }

      // 立即更新 Tab 状态（UI 响应）
      mengzhuMainTab.value = tab

      // 盟主趋势切换Tab时：先设置loading状态，再清空列表数据
      // 这样会显示加载中状态，而不是闪一下"暂无数据"
      loading.value = true
      poolList.value = []
      totalCount.value = 0

      // 防抖处理数据请求
      mainTabChangeTimer = setTimeout(() => {
        mainTabChangeTimer = null
        filterParams.pageNo = 1
        hasMore.value = true
        if (tab === 'hotCases') {
          // 切换到近30天热门案例时，重置排序状态（避免之前的排序影响显示）
          filterParams.sortField = ''
          filterParams.sortType = ''
          // 重置表格排序箭头显示
          if (poolTableRef.value && poolTableRef.value.resetSort) {
            poolTableRef.value.resetSort()
          }
          getMengzhuHotCases()
        } else {
          // 切换到信号池时，恢复默认排序（涨幅倒序）
          const fieldMap = getSortFieldMap(currentCourseNo.value)
          filterParams.sortField = fieldMap['pxChangRate'] || 'PxChangRate'
          filterParams.sortType = 'desc'
          // 重置表格排序箭头显示
          if (poolTableRef.value && poolTableRef.value.resetSort) {
            poolTableRef.value.resetSort()
          }
          getPoolData()
        }
      }, MAIN_TAB_CHANGE_DELAY)
    }

    // 获取盟主趋势近30天热门案例数据
    function getMengzhuHotCases() {
      // 增加请求序列号，用于处理快速切换时的竞态条件
      requestSequence++
      const currentSequence = requestSequence

      loading.value = true

      const data: any = {}
      data.data = {}
      data.headers = {
        clienttype: 'pc',
        appVersion: route.query.appVersion,
        token: route.query.token,
        "Content-Type": "application/json"
      }

      stockpool.getMengzhuTrendCard(data).then((res: any) => {
        // 检查请求序列号，如果不是最新请求则忽略响应（解决快速切换时的竞态条件）
        if (currentSequence !== requestSequence) {
          console.log(`[盟主趋势] 忽略旧的热门案例请求响应，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        // 再次检查当前 Tab 是否仍然是 hotCases，避免数据错乱
        if (mengzhuMainTab.value !== 'hotCases') {
          console.log(`[盟主趋势] 当前 Tab 已切换为 ${mengzhuMainTab.value}，忽略热门案例响应`)
          loading.value = false
          return
        }

        console.log('getMengzhuTrendCard res:', res)
        if (res.data) {
          // 数据直接在 res.data 数组中，不是 res.data.cardList
          const rawList = Array.isArray(res.data) ? res.data : (res.data.cardList || res.data || [])
          const newData = rawList.map((item: any, index: number) => {
            // 从 symbol 中提取 market（如果没有 market 字段）
            // symbol 格式可能是 "600000" 或 "sh600000"
            let market = item.market || ''
            if (!market && item.symbol) {
              // 根据股票代码判断市场
              const code = item.symbol
              if (code.startsWith('6') || code.startsWith('5')) {
                market = 'sh'
              } else if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) {
                market = 'sz'
              } else if (code.startsWith('4') || code.startsWith('8')) {
                market = 'bj'
              }
            }
            return {
              ...item,
              market: market,
              index: index + 1,
              // 字段映射：保留原始的 pxChangeRate 作为涨幅，cumRate 作为累计涨幅
              pxChangRate: item.pxChangeRate || 0 // 涨幅（通过 WebSocket 实时更新）
            }
          })
          poolList.value = newData
          totalCount.value = rawList.length
          hasMore.value = false // 近30天热门案例不分页

          // 打印第一条数据，用于调试 WebSocket 匹配
          if (newData.length > 0) {
            console.log('[盟主趋势热门案例] 第一条数据:', JSON.stringify(newData[0], null, 2))
          }

          // 更新时间（如果有）
          if (res.data.updateTime) {
            updateTime.value = formatMengzhuUpdateTime(res.data.updateTime)
          }
        }
        loading.value = false
      }).catch((err: any) => {
        // 检查请求序列号，如果不是最新请求则忽略错误处理
        if (currentSequence !== requestSequence) {
          console.log(`[盟主趋势] 忽略旧的热门案例请求错误，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }
        console.error('getMengzhuTrendCard failed', err)
        loading.value = false
      })
    }

    // 去重函数：根据 symbol 字段过滤掉已存在的股票
    // 用于滚动加载时避免出现重复股票
    function deduplicateStocks(existingList: any[], newList: any[]): any[] {
      if (!newList || newList.length === 0) {
        return []
      }
      // 直接返回新列表，不做去重处理
      return newList.filter((item: any) => item != null)
    }

    // 重置列表状态
    function resetListState() {
      filterParams.pageNo = 1
      poolList.value = [] as any
      hasMore.value = true
    }

    // 刷新 - 默认按涨幅倒序排序
    function handleRefresh() {
      resetListState()
      // 设置默认排序：涨幅倒序
      const fieldMap = getSortFieldMap(currentCourseNo.value)
      filterParams.sortField = fieldMap['pxChangRate'] || 'PxChangRate'
      filterParams.sortType = 'desc'
      // 重置表格排序箭头显示
      if (poolTableRef.value && poolTableRef.value.resetSort) {
        poolTableRef.value.resetSort()
      }
      getPoolData()
    }

    // 筛选变化
    function handleFilterChange(filterData: { qs: number; zj: number; tc: string }) {
      filterParams.qs = filterData.qs
      filterParams.zj = filterData.zj
      filterParams.tc = filterData.tc
      // 判断是否有筛选条件
      filterParams.isChose = (filterData.qs !== 0 || filterData.zj !== 0 || filterData.tc !== '0') ? 1 : 0
      resetListState()
      getPoolData()
    }

    // 日期变化
    function handleDateChange(date: string) {
      const timestamp = moment(date).valueOf()
      filterParams.tradeDay = String(timestamp)
      resetListState()
      getPoolData()
    }

    // 自选变化
    function handleOptionalChange(value: boolean) {
      filterParams.onlyOptional = value ? 1 : 0
      resetListState()
      getPoolData()
    }

    // 表格变化（排序）- 使用配置化的排序字段映射
    function handleTableChange({ sorter }: any) {
      if (sorter && sorter.field) {
        const fieldMap = getSortFieldMap(currentCourseNo.value)
        filterParams.sortField = fieldMap[sorter.field] || ''
        filterParams.sortType = sorter.order === 'ascend' ? 'asc' : 'desc'
      } else {
        // 取消排序时清空排序参数
        filterParams.sortField = ''
        filterParams.sortType = ''
      }
      resetListState()
      getPoolData()
    }

    // 股票单击选择（筹码大师搜索功能）
    // isClick=1 弹窗模式：单击不执行任何操作（只有双击才有效）
    // isClick=2 内嵌模式：单击执行设置焦点股票
    function handleStockSelect(item: any) {
      // isClick=1 弹窗模式：单击不执行任何操作
      if (isClick.value === 1) {
        console.log('筹码大师搜索单击（弹窗模式，不执行）')
        return
      }

      // isClick=2 内嵌模式：执行单击交互（设置焦点股票）
      const code = item.Code
      const market = (item.MarketCode || 'sz').toLowerCase()
      const clickUrl = `gw://funccall?funcname=OnFireStyleEvent;Command=set_focus_stock;param=<params><code>${code}</code><market_letter>${market}</market_letter></params>`
      window.open(clickUrl)
      console.log('筹码大师搜索单击:', clickUrl)
    }

    // 股票双击选择（筹码大师搜索功能）
    // isClick=1 和 isClick=2 都执行双击交互（设置股票列表、打开K线图、显示指标）
    function handleStockDblClick(item: any) {
      const code = item.Code
      const market = (item.MarketCode || 'sz').toLowerCase()

      setTimeout(() => {
        // 构建股票列表：将选中的股票放到最前面，其余股票按原顺序排列
        const selectedSymbol = code
        const clickedIndex = poolList.value.findIndex((listItem: any) => listItem.symbol === selectedSymbol)

        let stockList = ''
        if (clickedIndex >= 0) {
          // 如果选中的股票在列表中，重新排序：选中的放最前面
          const afterClicked = poolList.value.slice(clickedIndex + 1)
          const beforeClicked = poolList.value.slice(0, clickedIndex)
          const reorderedList = [poolList.value[clickedIndex]].concat(afterClicked).concat(beforeClicked)
          stockList = reorderedList.map((listItem: any) => `${listItem.market || 'sz'}:${listItem.symbol}`).join(',')
        } else {
          // 如果选中的股票不在列表中（搜索到的新股票），将其放到最前面
          const existingList = poolList.value.map((listItem: any) => `${listItem.market || 'sz'}:${listItem.symbol}`).join(',')
          stockList = existingList ? `${market}:${code},${existingList}` : `${market}:${code}`
        }

        // 第一个调用：设置股票列表
        const url1 = `gw://funccall?funcname=SetCurStockList;list=${stockList}`
        window.open(url1)
        console.log('筹码大师搜索双击 url1:', url1)

        // 第二个调用：打开K线图
        setTimeout(() => {
          const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style></params>`
          window.open(url2)
          console.log('筹码大师搜索双击 url2:', url2)

          // 第三个调用：显示指标
          setTimeout(() => {
            const indAlias = (productData.value && productData.value[0]?.poolName) || currentCourseConfig.value.courseName
            const url3 = `gw://funccall?funcname=OnFireCommand;Command=yin_ind_show;param=<params><ind_alias>${indAlias}</ind_alias><ingnore_paras>${1}</ingnore_paras></params>`
            window.open(url3)
            console.log('筹码大师搜索双击 url3:', url3)
          }, 100)
        }, 100)
      }, 50)
    }

    // 初始化加载
    onMounted(async () => {
      fetchProductData()

      // 设置默认排序：涨幅倒序
      const fieldMap = getSortFieldMap(currentCourseNo.value)
      filterParams.sortField = fieldMap['pxChangRate'] || 'PxChangRate'
      filterParams.sortType = 'desc'

      getPoolData()

      // 初始化时也获取交易日列表（使用当前时间戳）
      const currentTimestamp = Math.floor(Date.now() / 1000)
      await fetchTradingDays(currentTimestamp)
    })

    // 排序字段映射：接口字段名 -> 前端数据字段名
    const sortFieldToDataKey: Record<string, string> = {
      // 四维猎手字段映射
      'ClosePx': 'closePx',
      'LastPx': 'lastPx',
      'PxChangRate': 'pxChangRate',
      'HighRate': 'highRate',
      'Macd': 'macd',
      'AmountDif': 'amount',
      'PlateType': 'plateType',
      'Volume': 'volume',
      'Turnover': 'turnover',
      'TurnoverRate': 'turnoverRate',
      // 箱体擒龙字段映射
      'lastPx': 'lastPx',
      'pxChangeRate': 'pxChangRate',
      'highPct': 'highPct',
      'businessBalance': 'businessBalance',
      'turnoverRatio': 'turnoverRatio',
      'selectedPrice': 'selectedPrice',
      // 筹码大师字段映射
      'state': 'state',
      // 盟主趋势字段映射
      'PxChangeRate': 'pxChangRate',
      'BuyPx': 'buyPx',
      'MaxRate': 'maxRate',
      'CumRate': 'cumRate',
      'Amount': 'amount',
      'FundStatus': 'fundStatus',
      'SellTime': 'sellTime'
    }

    // 根据当前排序条件对列表进行排序
    function sortPoolList() {
      if (!filterParams.sortField || !filterParams.sortType) return

      const dataKey = sortFieldToDataKey[filterParams.sortField]
      if (!dataKey) return

      const isAsc = filterParams.sortType === 'asc'

      poolList.value.sort((a: any, b: any) => {
        const valA = a[dataKey] ?? 0
        const valB = b[dataKey] ?? 0

        // 盟主资金(fundStatus)特殊处理：值越小越好（1=多头强势最好，4=空头偏强最差）
        // 所以降序时应该是小的在前面，升序时大的在前面（与普通数值排序相反）
        if (dataKey === 'fundStatus') {
          if (isAsc) {
            return valB - valA  // 升序：大的在前（差的在前）
          } else {
            return valA - valB  // 降序：小的在前（好的在前）
          }
        }

        // 其他字段正常排序
        if (isAsc) {
          return valA - valB
        } else {
          return valB - valA
        }
      })
    }

    function websocketonmessage(event: any) {
      // 更新列表数据（列表订阅 onHQLIST 格式）
      if (poolList.value && poolList.value.length) {
        try {
          const wsData = JSON.parse(event.data)
          console.log('WebSocket收到列表数据:', wsData)

          // 列表订阅返回的数据在 QuoteData.DynaData 数组中
          const dynaDataList = wsData?.QuoteData?.DynaData || []

          if (dynaDataList.length) {
            // 打印第一条数据的完整结构，用于调试
            console.log('第一条推送数据结构:', JSON.stringify(dynaDataList[0], null, 2))

            // 判断是否为箱体擒龙
            const isBoxDragon = currentCourseNo.value === '33130259066e4650bd0b7cf6b23d9488'
            // 判断是否为盟主趋势
            const isMengzhuTrendProduct = currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f'

            // 盟主趋势近30天热门案例调试日志
            if (isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases' && poolList.value.length > 0) {
              console.log('[盟主趋势热门案例] poolList第一条数据:', {
                symbol: poolList.value[0].symbol,
                market: poolList.value[0].market,
                lastPx: poolList.value[0].lastPx,
                pxChangRate: poolList.value[0].pxChangRate
              })
              console.log('[盟主趋势热门案例] WebSocket第一条数据:', {
                InstrumentID: dynaDataList[0].InstrumentID,
                Inst: dynaDataList[0].Inst,
                ExchangeID: dynaDataList[0].ExchangeID,
                Market: dynaDataList[0].Market,
                LastPrice: dynaDataList[0].LastPrice,
                UpdownRate: dynaDataList[0].UpdownRate,
                Amount: dynaDataList[0].Amount
              })
            }

            // 市场代码标准化函数：将各种格式的市场代码统一为小写的 sh/sz/bj
            const normalizeMarket = (market: string): string => {
              const m = (market || '').toLowerCase()
              // 处理各种可能的市场代码格式
              if (m === 'sh' || m === 'sse' || m === '1' || m === 'sha') return 'sh'
              if (m === 'sz' || m === 'szse' || m === '0' || m === 'sza') return 'sz'
              if (m === 'bj' || m === 'bse' || m === '2') return 'bj'
              return m
            }

            poolList.value.forEach((item: any) => {
              // 在推送数据中查找匹配的股票
              dynaDataList.forEach((msgItem: any) => {
                const msgInst = msgItem.InstrumentID || msgItem.Inst
                const msgMarketRaw = msgItem.ExchangeID || msgItem.Market || ''
                const msgMarket = normalizeMarket(msgMarketRaw)
                const itemMarket = normalizeMarket(item.market || '')

                // 匹配条件：symbol 相同，且 market 相同（或者 item 没有 market 时只匹配 symbol）
                const symbolMatch = item.symbol === msgInst
                const marketMatch = !itemMarket || itemMarket === msgMarket

                // 盟主趋势热门案例：打印匹配详情（仅前3条）
                if (isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases') {
                  const itemIndex = poolList.value.indexOf(item)
                  if (itemIndex < 3 && symbolMatch) {
                    console.log(`[盟主趋势热门案例] 匹配检查 #${itemIndex}:`, {
                      itemSymbol: item.symbol,
                      msgInst,
                      symbolMatch,
                      itemMarket,
                      msgMarket,
                      msgMarketRaw,
                      marketMatch,
                      finalMatch: symbolMatch && marketMatch
                    })
                  }
                }

                if (symbolMatch && marketMatch) {
                  // 更新最新价
                  if (msgItem.LastPrice !== undefined && msgItem.LastPrice !== null) {
                    item.lastPx = msgItem.LastPrice
                  }

                  // 更新涨幅（UpdownRate 是小数形式，如 0.05 表示 5%）
                  if (msgItem.UpdownRate !== undefined && msgItem.UpdownRate !== null) {
                    item.pxChangRate = msgItem.UpdownRate
                  } else if (msgItem.Updown !== undefined && msgItem.Updown !== null) {
                    item.pxChangRate = msgItem.Updown
                  }

                  // 更新成交量
                  if (msgItem.Volume !== undefined && msgItem.Volume !== null) {
                    item.volume = msgItem.Volume
                  }

                  // 更新成交额
                  if (msgItem.Amount !== undefined && msgItem.Amount !== null) {
                    if (isBoxDragon) {
                      // 箱体擒龙使用 businessBalance 字段
                      item.businessBalance = msgItem.Amount
                    } else if (isMengzhuTrendProduct) {
                      // 盟主趋势使用 amount 字段（和四维猎手的 turnover 类似）
                      item.amount = msgItem.Amount
                    } else {
                      // 四维猎手使用 turnover 字段
                      item.turnover = msgItem.Amount
                    }
                  }

                  // 更新换手率（箱体擒龙特有）
                  if (isBoxDragon && msgItem.TurnoverRate !== undefined && msgItem.TurnoverRate !== null) {
                    item.turnoverRatio = msgItem.TurnoverRate
                  }
                }
              })
            })

            // 如果有排序条件，更新数据后重新排序
            // 注意：盟主趋势的近30天热门案例不需要排序，保持接口返回的顺序
            if (filterParams.sortField && filterParams.sortType && !(isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases')) {
              sortPoolList()
            }
          }
        } catch (e) {
          console.error('websocketonmessage parse error:', e)
        }
      }
    }

    return {
      // ==================== 组件引用 ====================
      poolTableRef,

      // ==================== 基础数据 ====================
      productData,
      activeTab,
      poolList,
      loading,
      updateTime,
      historyDates,
      hasMore,
      loadingMore,
      theme,
      totalCount,

      // ==================== 课程/产品配置 ====================
      currentCourseNo,
      currentCourseConfig,
      currentWsListKey,
      isClick,
      isChipMaster,
      isMengzhuTrend,
      mengzhuMainTab,

      // ==================== 计算属性（简化模板） ====================
      hasProductData,
      getProductCardProps,
      filterPanelProps,
      poolTableProps,

      // ==================== 事件处理方法 ====================
      fresh,
      websocketonmessage,
      handleFilterChange,
      handleDateChange,
      handleOptionalChange,
      handleTableChange,
      handleSignalTypeChange,
      handleRefresh,
      handleLoadMore,
      handleStockSelect,
      handleStockDblClick,
      handleMainTabChange,
      getMengzhuHotCases
    }
  }
})
